// Minimal DOM helpers. No framework, no JSX — keep bundle tiny.

type ElAttrs<K extends keyof HTMLElementTagNameMap> = {
  [P in keyof HTMLElementTagNameMap[K]]?: HTMLElementTagNameMap[K][P] extends CSSStyleDeclaration
    ? string
    : HTMLElementTagNameMap[K][P];
} & {
  class?: string;
  dataset?: Record<string, string>;
  // Arbitrary HTML/ARIA attributes set via setAttribute.
  [attr: `aria-${string}`]: string | undefined;
  [attr: `data-${string}`]: string | undefined;
  htmlFor?: string;
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: ElAttrs<K> = {},
  children: (Node | string | null | undefined | false)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'style') node.setAttribute('style', String(v));
    else if (k === 'htmlFor') (node as unknown as HTMLLabelElement).htmlFor = String(v);
    else if (k === 'dataset' && typeof v === 'object') {
      for (const [dk, dv] of Object.entries(v as Record<string, string>)) node.dataset[dk] = dv;
    } else if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k.startsWith('aria-') || k.startsWith('data-')) {
      node.setAttribute(k, String(v));
    } else {
      // biome-ignore lint/suspicious/noExplicitAny: dynamic html prop assignment
      (node as any)[k] = v;
    }
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export function mount(content: HTMLElement | DocumentFragment) {
  const root = document.getElementById('app');
  if (!root) throw new Error('#app missing');
  root.replaceChildren(content);
}

export function clear(node: HTMLElement) {
  node.replaceChildren();
}

export function toast(message: string, ms = 1800) {
  const t = el('div', { class: 'toast', textContent: message });
  document.body.append(t);
  setTimeout(() => t.remove(), ms);
}

export function uuid(): string {
  return crypto.randomUUID();
}
